'use client'

import { useEffect, useMemo, useRef } from 'react'

import { useCurrentNavItem } from '@/hooks/useCurrentNavItem'
import { usePathname } from '@/navigation'
import { trackUsageEvent } from '@/utils/posthog.utils'

const UUID_SEGMENT =
  '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'
const UUID_PATTERN = new RegExp(`^${UUID_SEGMENT}$`)
const KEYED_UUID_PATTERN = new RegExp(`^(.*)-${UUID_SEGMENT}$`)
const NUMERIC_PATTERN = /^\d+$/

function normalizeScreenSegment(segment: string): string {
  if (
    UUID_PATTERN.test(segment) ||
    KEYED_UUID_PATTERN.test(segment) ||
    NUMERIC_PATTERN.test(segment)
  ) {
    return '[id]'
  }

  return segment.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase()
}

export function resolveScreenKey(
  pathname: string | undefined,
  navItemKey?: string,
): string {
  if (navItemKey) return navItemKey
  if (!pathname || pathname === '/') return 'dashboard'

  const normalizedSegments = pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => normalizeScreenSegment(segment))

  return normalizedSegments.join('.') || 'dashboard'
}

export function PostHogScreenTracker() {
  const pathname = usePathname()
  const { currentNavItem } = useCurrentNavItem()
  const lastTrackedScreenKeyRef = useRef<string | null>(null)

  const screenKey = useMemo(
    () => resolveScreenKey(pathname, currentNavItem?.key),
    [currentNavItem?.key, pathname],
  )

  useEffect(() => {
    if (!screenKey || lastTrackedScreenKeyRef.current === screenKey) return

    lastTrackedScreenKeyRef.current = screenKey
    trackUsageEvent('screen_viewed', {
      screen_key: screenKey,
      surface: 'route',
    })
  }, [screenKey])

  return null
}
